import React, { useEffect, useState } from "react";
import { Modal, Select, Button, Table, Typography, Flex, Tag } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { PanelUser } from "../../actions/superuser";
import { UserOrganization } from "../../types";

const { Text } = Typography;

// Fixed for every membership this panel writes. Shown so it is clear what is
// being granted, but not editable - the backend sets both regardless of what is
// sent, and offering a choice the server overrides would be a lie.
const ROLE = "member";
const PRODUCT = "Scout";

/**
 * Manage a user's Scout organizations.
 *
 * Nothing is written until Save. Removing every organization is a legitimate
 * outcome - it returns the user to Unaffiliated - so it is allowed, with the
 * consequence spelled out rather than blocked.
 */
const ManagePermissionsModal: React.FC<{
  user: PanelUser | null;
  organizations: UserOrganization[];
  onClose: () => void;
  onSave: (user: PanelUser, orgs: string[]) => Promise<unknown>;
  saving: boolean;
}> = ({ user, organizations, onClose, onSave, saving }) => {
  const [orgs, setOrgs] = useState<string[]>([]);

  // Reset whenever a different user is opened, so an abandoned edit cannot leak
  // into the next one.
  useEffect(() => {
    setOrgs((user?.memberships || []).map((m) => m.org));
  }, [user]);

  if (!user) return null;

  const nameFor = (uuid: string) =>
    organizations.find((o) => o.uuid === uuid)?.display_name || uuid;

  const original = (user.memberships || []).map((m) => m.org);
  const dirty =
    orgs.length !== original.length || orgs.some((o) => !original.includes(o));

  const available = organizations
    .filter((o) => !orgs.includes(o.uuid))
    .map((o) => ({ value: o.uuid, label: o.display_name || o.uuid }));

  return (
    <Modal
      open
      title={`Manage Permissions — ${user.name || user.email}`}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          disabled={!dirty}
          loading={saving}
          onClick={async () => {
            await onSave(user, orgs);
            onClose();
          }}
        >
          Save
        </Button>,
      ]}
    >
      <Text type="secondary">
        {orgs.length === 0
          ? "This user belongs to no Scout organizations and will appear under Unaffiliated."
          : "Organizations this user belongs to in Scout."}
      </Text>

      {orgs.length > 0 && (
        <Table
          className="mt-3"
          size="small"
          pagination={false}
          rowKey={(r) => r}
          dataSource={orgs}
          columns={[
            {
              title: "Organization",
              render: (_: unknown, uuid: string) => nameFor(uuid),
            },
            {
              title: "Role",
              render: () => <Tag>{ROLE}</Tag>,
            },
            {
              title: "Product",
              render: () => <Tag>{PRODUCT}</Tag>,
            },
            {
              title: "",
              align: "right" as const,
              render: (_: unknown, uuid: string) => (
                <Button
                  type="text"
                  size="small"
                  aria-label={`Remove ${nameFor(uuid)}`}
                  icon={<DeleteOutlined />}
                  onClick={() => setOrgs(orgs.filter((o) => o !== uuid))}
                />
              ),
            },
          ]}
        />
      )}

      <Flex gap={8} className="mt-4">
        <Select
          className="flex-1"
          placeholder={
            available.length === 0
              ? "Already in every organization"
              : "Add organization"
          }
          disabled={available.length === 0}
          options={available}
          value={null}
          onChange={(uuid: string) => setOrgs([...orgs, uuid])}
          showSearch
          optionFilterProp="label"
        />
      </Flex>
    </Modal>
  );
};

export default ManagePermissionsModal;
